import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Plus, Play, Trash2, Power, Clock, Search, Loader2 } from 'lucide-react';
import {
  type ScrapeSchedule,
  type ScheduleScope,
  type ScheduleFrequency,
  type ScheduleRunResult,
  SCOPE_LABELS,
  FREQUENCY_LABELS,
  WEEKDAY_LABELS,
  computeNextRunIso,
  describeSchedule,
} from '../../utils/scrapeSchedule';
import { triggerScrapeCron } from '../../utils/triggerScrapeCron';

type ForumLite = { id: string; name: string; category: string | null; is_active: boolean | null };

type DraftSchedule = {
  id: string | null;
  name: string;
  scope: ScheduleScope;
  forum_ids: string[];
  frequency: ScheduleFrequency;
  run_at_hour: number;
  run_at_minute: number;
  run_at_weekday: number;
  run_at_day: number;
  enabled: boolean;
};

const emptyDraft = (): DraftSchedule => ({
  id: null,
  name: '',
  scope: 'active',
  forum_ids: [],
  frequency: 'weekly',
  run_at_hour: 3,
  run_at_minute: 0,
  run_at_weekday: 1,
  run_at_day: 1,
  enabled: true,
});

const fmtDate = (iso: string | null) =>
  iso ? format(new Date(iso), 'dd MMM yyyy HH:mm', { locale: tr }) : '—';

const ScheduleManager: React.FC = () => {
  const { darkMode } = useTheme();
  const [schedules, setSchedules] = useState<ScrapeSchedule[]>([]);
  const [forums, setForums] = useState<ForumLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<DraftSchedule | null>(null);
  const [forumQuery, setForumQuery] = useState('');
  const [tableMissing, setTableMissing] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);

  const box = darkMode ? 'bg-gray-800 text-gray-200 border-gray-700' : 'bg-white text-gray-800 border-gray-200';
  const input = `border rounded-lg px-3 py-2 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'}`;

  const load = useCallback(async () => {
    setLoading(true);
    const [schedRes, forumRes] = await Promise.all([
      supabase.from('scrape_schedules').select('*').order('created_at', { ascending: true }),
      supabase.from('forums').select('id, name, category, is_active').order('name', { ascending: true }),
    ]);
    if (schedRes.error) {
      // Tablo henüz kurulmamış olabilir.
      if (/relation|does not exist|schema cache/i.test(schedRes.error.message)) {
        setTableMissing(true);
      } else {
        toast.error(schedRes.error.message);
      }
      setSchedules([]);
    } else {
      setTableMissing(false);
      setSchedules((schedRes.data as ScrapeSchedule[]) ?? []);
    }
    setForums((forumRes.data as ForumLite[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredForums = useMemo(() => {
    const q = forumQuery.trim().toLocaleLowerCase('tr');
    if (!q) return forums;
    return forums.filter(
      (f) => f.name.toLocaleLowerCase('tr').includes(q) || (f.category ?? '').toLocaleLowerCase('tr').includes(q)
    );
  }, [forums, forumQuery]);

  const startCreate = () => {
    setForumQuery('');
    setDraft(emptyDraft());
  };

  const startEdit = (s: ScrapeSchedule) => {
    setForumQuery('');
    setDraft({
      id: s.id,
      name: s.name,
      scope: s.scope,
      forum_ids: s.forum_ids ?? [],
      frequency: s.frequency,
      run_at_hour: s.run_at_hour ?? 3,
      run_at_minute: s.run_at_minute ?? 0,
      run_at_weekday: s.run_at_weekday ?? 1,
      run_at_day: s.run_at_day ?? 1,
      enabled: s.enabled,
    });
  };

  const saveDraft = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error('Zamanlamaya bir ad verin.');
      return;
    }
    if (draft.scope === 'selected' && draft.forum_ids.length === 0) {
      toast.error('Seçili kapsam için en az bir forum seçin.');
      return;
    }
    const next_run_at = draft.enabled
      ? computeNextRunIso(
          draft.frequency,
          draft.run_at_hour,
          draft.run_at_weekday,
          draft.run_at_day,
          draft.run_at_minute
        )
      : null;

    const payload = {
      name: draft.name.trim(),
      scope: draft.scope,
      forum_ids: draft.scope === 'selected' ? draft.forum_ids : [],
      frequency: draft.frequency,
      run_at_hour: draft.run_at_hour,
      run_at_minute: draft.run_at_minute,
      run_at_weekday: draft.frequency === 'weekly' ? draft.run_at_weekday : null,
      run_at_day: draft.frequency === 'monthly' ? draft.run_at_day : null,
      enabled: draft.enabled,
      next_run_at,
      updated_at: new Date().toISOString(),
    };

    const res = draft.id
      ? await supabase.from('scrape_schedules').update(payload).eq('id', draft.id)
      : await supabase.from('scrape_schedules').insert(payload);

    if (res.error) {
      toast.error(res.error.message);
      return;
    }
    toast.success(draft.id ? 'Zamanlama güncellendi.' : 'Zamanlama oluşturuldu.');
    setDraft(null);
    void load();
  };

  const toggleEnabled = async (s: ScrapeSchedule) => {
    const enabled = !s.enabled;
    const next_run_at = enabled
      ? computeNextRunIso(s.frequency, s.run_at_hour, s.run_at_weekday, s.run_at_day, s.run_at_minute)
      : null;
    const { error } = await supabase
      .from('scrape_schedules')
      .update({ enabled, next_run_at, updated_at: new Date().toISOString() })
      .eq('id', s.id);
    if (error) toast.error(error.message);
    else void load();
  };

  const runNow = async (s: ScrapeSchedule) => {
    if (runningId) return;
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) {
      toast.error('Oturum bulunamadı. Yeniden giriş yapın.');
      return;
    }

    setRunningId(s.id);
    const toastId = toast.loading(
      `"${s.name}" çalışıyor… (${s.scope === 'selected' ? s.forum_ids?.length ?? 0 : 'birçok'} forum, parçalı)`
    );
    try {
      let incomplete = true;
      let lastRan: ScheduleRunResult | undefined;
      let rounds = 0;
      while (incomplete && rounds < 80) {
        rounds += 1;
        const result = await triggerScrapeCron(s.id, token);
        const entry = result.schedules?.[0];
        lastRan = entry?.result;
        incomplete = Boolean(entry?.incomplete ?? entry?.result?.incomplete);
        if (incomplete && lastRan) {
          const done = lastRan.offset ?? lastRan.ran ?? 0;
          const total = lastRan.total ?? '?';
          toast.loading(`Devam ediyor… ${done}/${total} · ✓${lastRan.ok} ✗${lastRan.failed}`, {
            id: toastId,
          });
        }
      }
      if (lastRan) {
        const errMsg = lastRan.message || lastRan.errors?.[0]?.error;
        const nothingRan =
          (lastRan.total ?? 0) === 0 ||
          ((lastRan.ran ?? 0) === 0 && (lastRan.failed ?? 0) > 0 && (lastRan.ok ?? 0) === 0);
        if (nothingRan) {
          toast.error(errMsg || 'Hiç forum işlenmedi. Seçili forumları kontrol edin.', {
            id: toastId,
            duration: 12000,
          });
        } else {
          const suffix = lastRan.incomplete
            ? ` (yarıda kaldı: ${lastRan.offset ?? 0}/${lastRan.total ?? '?'})`
            : '';
          toast.success(`Tamamlandı: ✓${lastRan.ok} ✗${lastRan.failed} ⤼${lastRan.skipped}${suffix}`, {
            id: toastId,
            duration: 8000,
          });
        }
      } else {
        toast.success('Zamanlama çalıştırıldı.', { id: toastId });
      }
      void load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Cron çalıştırılamadı';
      toast.error(msg, { id: toastId, duration: 10000 });
      void load();
    } finally {
      setRunningId(null);
    }
  };

  const removeSchedule = async (s: ScrapeSchedule) => {
    if (!window.confirm(`"${s.name}" zamanlaması silinsin mi?`)) return;
    const { error } = await supabase.from('scrape_schedules').delete().eq('id', s.id);
    if (error) toast.error(error.message);
    else {
      toast.success('Silindi.');
      void load();
    }
  };

  const toggleForumInDraft = (id: string) => {
    setDraft((d) => {
      if (!d) return d;
      const has = d.forum_ids.includes(id);
      return { ...d, forum_ids: has ? d.forum_ids.filter((x) => x !== id) : [...d.forum_ids, id] };
    });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-2xl font-bold">Zamanlamalar (cron)</h2>
        <button
          type="button"
          onClick={startCreate}
          className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm"
        >
          <Plus size={16} /> Yeni zamanlama
        </button>
      </div>

      <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <strong>Şimdi</strong> düğmesi zamanlamayı anında çalıştırır (çok forum parçalı işlenir).
        Otomatik çalışma için harici cron{' '}
        <code>scrape-cron-runner.php?key=GIZLI_ANAHTAR</code> URL&apos;ini <strong>15 dakikada bir</strong> tetiklemelidir
        (günde bir kez yetmez — 389 forum birkaç turda biter).
        Kurulum: <code>scripts/KURULUM-cron.sql</code> + <code>CRON-KURULUM.md</code>.
      </p>

      {schedules.some((s) => {
        if (!s.enabled || !s.next_run_at) return false;
        return new Date(s.next_run_at).getTime() < Date.now() - 60 * 60 * 1000;
      }) && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            darkMode ? 'bg-red-950/40 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <strong>Zamanlama gecikmiş</strong> — «Sonraki» tarihi geçmişte. Harici cron URL&apos;si tetiklenmiyor
          veya sunucu 389 forumu tek seferde bitiremeden kesiliyor olabilir. Önce{' '}
          <strong>Şimdi</strong> ile manuel başlatın; kalıcı çözüm için cron-job.org / cPanel&apos;de runner&apos;ı
          15 dakikada bir çağırın ve güncel <code>scrape-cron-lib.php</code> yükleyin.
        </div>
      )}

      {schedules.some((s) => s.enabled && !s.last_run_at) && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            darkMode ? 'bg-amber-950/40 border-amber-800 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          <strong>Otomatik cron henüz çalışmamış görünüyor</strong> (Son: —).
          Günlük zamanlamaların kendiliğinden işlemesi için cron-job.org veya cPanel cron ile{' '}
          <code>scrape-cron-runner.php</code> URL&apos;ini kurun. Manuel test için <strong>Şimdi</strong> kullanın.
        </div>
      )}

      {tableMissing && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            darkMode ? 'bg-amber-950/40 border-amber-800 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          <strong>scrape_schedules</strong> tablosu bulunamadı. Supabase → SQL Editor'de{' '}
          <code>scripts/KURULUM-cron.sql</code> dosyasını çalıştırın, sonra bu sayfayı yenileyin.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
        </div>
      ) : (
        <div className="grid gap-3">
          {schedules.length === 0 && !tableMissing && (
            <div className={`rounded-xl border p-6 text-sm ${box}`}>Henüz zamanlama yok. "Yeni zamanlama" ile ekleyin.</div>
          )}
          {schedules.map((s) => {
            const r = s.last_result;
            const overdue =
              s.enabled && s.next_run_at != null && new Date(s.next_run_at).getTime() < Date.now() - 60 * 60 * 1000;
            const incomplete = Boolean(r?.incomplete);
            return (
              <div key={s.id} className={`rounded-xl border p-4 ${box}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                      <h3 className="font-semibold truncate">{s.name}</h3>
                      {overdue && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-600 text-white">Gecikmiş</span>
                      )}
                      {incomplete && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-500 text-white">
                          Devam ediyor {r?.offset ?? 0}/{r?.total ?? '?'}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <Clock size={12} className="inline -mt-0.5 mr-1" />
                      {describeSchedule(s)} · {SCOPE_LABELS[s.scope]}
                      {s.scope === 'selected' ? ` (${s.forum_ids?.length ?? 0})` : ''}
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Sonraki: {fmtDate(s.next_run_at)} · Son: {fmtDate(s.last_run_at)}
                      {r ? ` · ✓${r.ok} ✗${r.failed} ⤼${r.skipped}` : ''}
                    </p>
                    {incomplete && r?.message && (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>{r.message}</p>
                    )}
                    {r && r.errors?.length > 0 && (
                      <details className="mt-1 text-xs">
                        <summary className="cursor-pointer text-red-400">{r.errors.length} hata</summary>
                        <ul className="mt-1 space-y-0.5">
                          {r.errors.map((e, i) => (
                            <li key={i} className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                              <strong>{e.forum}:</strong> {e.error}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => runNow(s)}
                      disabled={runningId === s.id}
                      className="px-2.5 py-1 text-xs rounded bg-green-600 text-white inline-flex items-center gap-1 disabled:opacity-60"
                    >
                      {runningId === s.id ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                      {runningId === s.id ? 'Çalışıyor…' : 'Şimdi'}
                    </button>
                    <button type="button" onClick={() => toggleEnabled(s)} className={`px-2.5 py-1 text-xs rounded inline-flex items-center gap-1 ${s.enabled ? 'bg-gray-500 text-white' : 'bg-indigo-600 text-white'}`}>
                      <Power size={13} /> {s.enabled ? 'Durdur' : 'Etkinleştir'}
                    </button>
                    <button type="button" onClick={() => startEdit(s)} className={`px-2.5 py-1 text-xs rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      Düzenle
                    </button>
                    <button type="button" onClick={() => removeSchedule(s)} className="px-2.5 py-1 text-xs rounded bg-red-600 text-white inline-flex items-center gap-1">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
          <div className={`w-full max-w-2xl rounded-xl border p-6 my-8 ${box}`}>
            <h3 className="text-lg font-bold mb-4">{draft.id ? 'Zamanlamayı düzenle' : 'Yeni zamanlama'}</h3>
            <div className="grid gap-4">
              <div>
                <label className="block text-sm mb-1">Ad</label>
                <input
                  className={`${input} w-full`}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Örn. Teknoloji forumları – günlük"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Kapsam</label>
                  <select className={`${input} w-full`} value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value as ScheduleScope })}>
                    {(Object.keys(SCOPE_LABELS) as ScheduleScope[]).map((k) => (
                      <option key={k} value={k}>{SCOPE_LABELS[k]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Sıklık</label>
                  <select className={`${input} w-full`} value={draft.frequency} onChange={(e) => setDraft({ ...draft, frequency: e.target.value as ScheduleFrequency })}>
                    {(Object.keys(FREQUENCY_LABELS) as ScheduleFrequency[]).map((k) => (
                      <option key={k} value={k}>{FREQUENCY_LABELS[k]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {draft.frequency !== 'hourly' && (
                  <div>
                    <label className="block text-sm mb-1">Saat (TR)</label>
                    <select
                      className={`${input} w-full`}
                      value={draft.run_at_hour}
                      onChange={(e) => setDraft({ ...draft, run_at_hour: Number(e.target.value) })}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {String(i).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm mb-1">
                    {draft.frequency === 'hourly' ? 'Dakika (her saat)' : 'Dakika'}
                  </label>
                  <select
                    className={`${input} w-full`}
                    value={draft.run_at_minute}
                    onChange={(e) => setDraft({ ...draft, run_at_minute: Number(e.target.value) })}
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
                {draft.frequency === 'weekly' && (
                  <div>
                    <label className="block text-sm mb-1">Gün</label>
                    <select
                      className={`${input} w-full`}
                      value={draft.run_at_weekday}
                      onChange={(e) => setDraft({ ...draft, run_at_weekday: Number(e.target.value) })}
                    >
                      {WEEKDAY_LABELS.map((w, i) => (
                        <option key={i} value={i}>
                          {w}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {draft.frequency === 'monthly' && (
                  <div>
                    <label className="block text-sm mb-1">Ayın günü</label>
                    <select
                      className={`${input} w-full`}
                      value={draft.run_at_day}
                      onChange={(e) => setDraft({ ...draft, run_at_day: Number(e.target.value) })}
                    >
                      {Array.from({ length: 28 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {draft.frequency !== 'hourly' && (
                <p className={`text-xs -mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Çalışma saati (TR):{' '}
                  <strong>
                    {String(draft.run_at_hour).padStart(2, '0')}:
                    {String(draft.run_at_minute).padStart(2, '0')}
                  </strong>
                </p>
              )}

              {draft.scope === 'selected' && (
                <div>
                  <label className="block text-sm mb-1">Forumlar ({draft.forum_ids.length} seçili)</label>
                  <div className={`relative mb-2`}>
                    <Search size={15} className="absolute left-2.5 top-2.5 text-gray-400" />
                    <input
                      className={`${input} w-full pl-8`}
                      value={forumQuery}
                      onChange={(e) => setForumQuery(e.target.value)}
                      placeholder="Forum ara…"
                    />
                  </div>
                  <div className={`max-h-56 overflow-y-auto rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    {filteredForums.map((f) => (
                      <label key={f.id} className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                        <input type="checkbox" checked={draft.forum_ids.includes(f.id)} onChange={() => toggleForumInDraft(f.id)} />
                        <span className="truncate">{f.name}</span>
                        <span className={`ml-auto text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{f.category ?? ''}{f.is_active === false ? ' · pasif' : ''}</span>
                      </label>
                    ))}
                    {filteredForums.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Eşleşme yok.</div>}
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                Etkin (kapalıyken cron atlanır)
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setDraft(null)} className={`px-4 py-2 rounded-lg text-sm ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                Vazgeç
              </button>
              <button type="button" onClick={saveDraft} className="px-4 py-2 rounded-lg text-sm bg-indigo-600 text-white">
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleManager;

